import { Checkbox } from "@legal-500/legal500-design-system";
import type { Feature } from "../../api/features.service";
import styles from "./FeaturePicker.module.css";

type FeatureCardProps = {
  feature: Feature;
  isSelected: boolean;
  onToggle: (featureCode: string) => void;
  disabled?: boolean;
  compact?: boolean;
};

export function FeatureCard({
  feature,
  isSelected,
  onToggle,
  disabled = false,
  compact = false,
}: FeatureCardProps) {
  const checkboxId = `feature-${feature.code}`;

  function handleCardClick() {
    if (!disabled) {
      onToggle(feature.code);
    }
  }

  function handleKeyDown(event: React.KeyboardEvent) {
    if (disabled) return;

    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      onToggle(feature.code);
    }
  }

  function handleCheckboxChange() {
    if (!disabled) {
      onToggle(feature.code);
    }
  }

  const cardClassName = [
    styles.featureCard,
    isSelected ? styles.selected : "",
    disabled ? styles.disabled : "",
    compact ? styles.compact : "",
  ]
    .filter(Boolean)
    .join(" ");

  return (
    <div
      className={cardClassName}
      role="button"
      tabIndex={disabled ? -1 : 0}
      onClick={handleCardClick}
      onKeyDown={handleKeyDown}
      aria-pressed={isSelected}
      aria-disabled={disabled}
    >
      <div className={styles.cardContent}>
        {compact ? (
          <div className={styles.compactContent}>
            <span className={styles.compactLabel}>
              {feature.name} <span className={styles.compactCode}>({feature.code})</span>
            </span>
          </div>
        ) : (
          <>
            <div className={styles.cardHeader}>
              <span className={styles.cardLabel}>{feature.name}</span>
              <span className={styles.cardCode}>{feature.code}</span>
            </div>
            <p className={styles.cardDescription}>{feature.description}</p>
          </>
        )}
      </div>

      <div className={styles.checkboxWrapper}>
        <Checkbox
          id={checkboxId}
          checked={isSelected}
          onChange={handleCheckboxChange}
          disabled={disabled}
          className={styles.checkbox}
          aria-label={`Select ${feature.name}`}
        />
      </div>
    </div>
  );
}
